import React, { useState } from 'react';
import { X, AlertTriangle, Trash2, Archive, XCircle, ShoppingCart } from 'lucide-react';
import Icon from '../../../components/AppIcon';


/**
 * DepotActionModal - handles Cancel, Archive, and Delete confirmations
 * actionType: 'cancel' | 'archive' | 'delete'
 */
export default function DepotActionModal({ deposit, actionType, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [deleteLinkedSale, setDeleteLinkedSale] = useState(false);
  const [processing, setProcessing] = useState(false);

  if (!deposit || !actionType) return null;

  const hasSaleLink = !!deposit?.sale_id;

  const config = {
    cancel: {
      title: 'Annuler le dépôt',
      icon: XCircle,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      btnColor: 'bg-orange-600 hover:bg-orange-700',
      btnLabel: 'Confirmer l\'annulation',
      description: `Voulez-vous vraiment annuler le dépôt de ${deposit?.client_name} ?`,
      warning: 'Le dépôt sera marqué "Annulé" et ne figurera plus dans les tournées actives. L\'historique sera conservé.',
      showReason: true,
    },
    archive: {
      title: 'Archiver le dépôt',
      icon: Archive,
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      btnColor: 'bg-gray-600 hover:bg-gray-700',
      btnLabel: 'Archiver',
      description: `Voulez-vous archiver le dépôt de ${deposit?.client_name} ?`,
      warning: 'Le dépôt sera déplacé dans les archives. Il ne sera plus visible dans la liste active mais restera consultable.',
      showReason: false,
    },
    delete: {
      title: 'Supprimer le dépôt',
      icon: Trash2,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      btnColor: 'bg-red-600 hover:bg-red-700',
      btnLabel: 'Oui, supprimer définitivement',
      description: `Voulez-vous vraiment supprimer le dépôt de ${deposit?.client_name} ?`,
      warning: 'Cette action est irréversible. Le dépôt et toutes les données associées (affectations chauffeur, notifications, événements) seront supprimés définitivement de Supabase.',
      showReason: false,
    },
  };

  const cfg = config?.[actionType];
  const Icon = cfg?.icon;

  const handleConfirm = async () => {
    setProcessing(true);
    try {
      await onConfirm({
        depositId: deposit?.id,
        saleId: deposit?.sale_id,
        actionType,
        reason: reason?.trim(),
        deleteLinkedSale: actionType === 'delete' && hasSaleLink ? deleteLinkedSale : false,
      });
      onClose();
    } catch (err) {
      console.error('Action failed:', err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cfg?.iconBg}`}>
              <Icon className={`w-5 h-5 ${cfg?.iconColor}`} />
            </div>
            <h3 className="text-lg font-bold text-gray-900">{cfg?.title}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-gray-800 text-sm">{cfg?.description}</p>

          <div className={`rounded-lg p-4 border ${actionType === 'delete' ? 'bg-red-50 border-red-200' : actionType === 'cancel' ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${actionType === 'delete' ? 'text-red-500' : actionType === 'cancel' ? 'text-orange-500' : 'text-gray-500'}`} />
              <p className={`text-sm ${actionType === 'delete' ? 'text-red-700' : actionType === 'cancel' ? 'text-orange-700' : 'text-gray-700'}`}>
                {cfg?.warning}
              </p>
            </div>
          </div>

          {/* Reason input for cancel */}
          {cfg?.showReason && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motif d'annulation (optionnel)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e?.target?.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                placeholder="Ex: Client indisponible, report demandé..."
              />
            </div>
          )}

          {/* Delete linked sale option */}
          {actionType === 'delete' && hasSaleLink && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-3">
                <ShoppingCart className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-yellow-800">Ce dépôt est lié à une commande client</p>
                  <p className="text-xs text-yellow-700 mt-1">Commande #{deposit?.sale_id?.slice(0, 8)?.toUpperCase()}</p>
                </div>
              </div>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="deleteScope"
                    checked={!deleteLinkedSale}
                    onChange={() => setDeleteLinkedSale(false)}
                    className="mt-0.5 text-red-600"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-800">Supprimer uniquement le dépôt</span>
                    <p className="text-xs text-gray-500">La commande client associée sera conservée</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="deleteScope"
                    checked={deleteLinkedSale}
                    onChange={() => setDeleteLinkedSale(true)}
                    className="mt-0.5 text-red-600"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-800">Supprimer le dépôt ET la commande client</span>
                    <p className="text-xs text-red-500">⚠️ La commande sera définitivement supprimée</p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            disabled={processing}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-60"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={processing}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg transition-colors font-medium disabled:opacity-60 ${cfg?.btnColor}`}
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Traitement...
              </>
            ) : (
              <>
                <Icon className="w-4 h-4" />
                {cfg?.btnLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
